/**
 * LegalPage.tsx  -  Renders any legal document by slug (/privacy, /terms, ...).
 * Public route. Pulls content from data/legalContent.ts.
 */

import { useEffect } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { LEGAL_DOCS } from '@/data/legalContent'
import LitlingsMark from '@/components/LitlingsMark'
import SiteFooter from '@/components/SiteFooter'

export default function LegalPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>()
  const slug = slugProp ?? params.slug ?? ''
  const doc = LEGAL_DOCS[slug]

  useEffect(() => { window.scrollTo(0, 0) }, [slug])

  if (!doc) return <Navigate to="/" replace />

  const otherDocs = Object.values(LEGAL_DOCS).filter((d) => d.slug !== slug)

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <nav className="sticky top-0 z-40 border-b border-portal-border bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2.5">
            <LitlingsMark size={28} />
            <span className="font-display text-lg font-extrabold">Litlings</span>
          </Link>
          <Link to="/" className="btn-ghost">
            Back to home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-5 py-14">
          <h1 className="mb-2 font-display text-4xl font-extrabold tracking-[-0.03em] sm:text-5xl">{doc.title}</h1>
          <p className="mb-8 text-sm text-portal-muted">Last updated: {doc.updated}</p>

          {doc.intro && (
            <p className="mb-10 text-lg leading-relaxed text-portal-text-soft">{doc.intro}</p>
          )}

          <div className="space-y-8">
            {doc.sections.map((s) => (
              <section key={s.heading}>
                <h2 className="mb-3 font-display text-xl font-bold">{s.heading}</h2>
                {s.paragraphs?.map((p, i) => (
                  <p key={i} className="mb-3 leading-relaxed text-portal-text-soft">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="space-y-2 mt-2">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 leading-relaxed text-portal-text-soft">
                        <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-red" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* Cross-links */}
          <div className="mt-14 border-t border-portal-border pt-8">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-portal-muted">More policies</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {otherDocs.map((d) => (
                <Link key={d.slug} to={`/${d.slug}`} className="font-semibold text-brand-blue hover:underline">
                  {d.title}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  )
}
